// 과목별 글 목록 - 단일 소스 (Single Source of Truth)
// SubjectBrowser와 각 과목 page.tsx에서 공유

export interface SubjectPost {
  title: string;
  href: string;
  scope: string;
  priority: string;
  /** "필수" 뱃지를 PostIcon 좌측 상단에 표시할지 여부 */
  essential?: boolean;
}

export interface SubjectData {
  name: string;
  image: string;
  href: string;
  recommendedPosts: SubjectPost[];
  posts: SubjectPost[];
}

// ─── 코딩 과목 ───

export const cLanguage: SubjectData = {
  name: "C언어",
  image: "/coding/c-language/c-language_gamja.webp",
  href: "/coding/c-language",
  recommendedPosts: [
    // 폴더 노출 순서: 각 scope의 첫 등장 인덱스 = 기초 → 연산자 → 제어문/반복문 → 함수 → 배열 → 문자열 → 포인터 → 메모리 → 구조체 → 자료구조 → 알고리즘
    // 입문 학습 흐름: 쉬운 것부터 어려운 것 순. 필수 4종(essential)은 PostIcon 좌측 상단에 별도 뱃지로 표시. 기초 scope는 priority 미부여
    { title: "코드를 읽는 순서 (C vs Java vs Python)", href: "/coding/common/code-reading-order", priority: "", scope: "기초", essential: true },
    { title: "변수 선언", href: "/coding/common/variable-declaration", priority: "", scope: "기초", essential: true },
    { title: "변수의 초기화", href: "/coding/common/initialization", priority: "", scope: "기초", essential: true },
    { title: "자료형 (int, char, float)", href: "/coding/c-language/data-types", priority: "🤔", scope: "기초", essential: true },
    { title: "형변환 (Casting)", href: "/coding/c-language/type-casting", priority: "🤔", scope: "기초" },
    // 기초 추가
    { title: "변수 스코프 (지역/전역 변수)", href: "/coding/common/variable-scope", priority: "", scope: "기초" },
    { title: "컴파일과 런타임", href: "/coding/common/compile-runtime", priority: "", scope: "기초" },
    // 연산자 폴더 첫 진입점
    { title: "산술·증감 연산자", href: "/coding/common/arithmetic-operator", priority: "🔥", scope: "연산자" },
    // 제어문/반복문 폴더 첫 진입점
    { title: "조건문 (if, if-else, else if)", href: "/coding/common/condition", priority: "⭐️", scope: "제어문/반복문" },
    { title: "반복문 (for, while)", href: "/coding/common/loop", priority: "⭐️", scope: "제어문/반복문" },
    // 함수 폴더 첫 진입점
    { title: "함수의 기초 (정의, 호출, 반환값)", href: "/coding/common/function", priority: "🤔", scope: "함수" },
    // 배열 폴더 첫 진입점
    { title: "배열의 기초", href: "/coding/common/array", priority: "🔥", scope: "배열" },
    // 문자열 폴더 첫 진입점
    { title: "문자열의 기초", href: "/coding/common/string", priority: "🔥", scope: "문자열" },
    // 포인터 폴더 첫 진입점 (코딩 문제에서 가장 중요)
    { title: "포인터의 기초", href: "/coding/c-language/pointer", priority: "💯", scope: "포인터" },
    // 메모리 폴더 첫 진입점
    { title: "동적 메모리 할당 (malloc, free)", href: "/coding/c-language/dynamic-memory", priority: "🔥", scope: "메모리" },
    // 구조체 폴더 첫 진입점
    { title: "구조체", href: "/coding/c-language/struct", priority: "⭐️", scope: "구조체" },
  ],
  posts: [
    // 기초 (나머지)
    { title: "헤더 파일과 #include", href: "/coding/c-language/header-file", priority: "🤔", scope: "함수" },
    // 입출력함수
    { title: "출력 함수 (putchar, printf, puts)", href: "/coding/c-language/output-functions", priority: "🤔", scope: "입출력함수" },
    { title: "입력 함수 (scanf, getchar, gets)", href: "/coding/c-language/input-functions", priority: "🤔", scope: "입출력함수" },
    // 연산자 (나머지)
    { title: "대입·복합 대입 연산자", href: "/coding/common/assignment-operator", priority: "🔥", scope: "연산자" },
    { title: "비교 연산자", href: "/coding/common/comparison-operator", priority: "🔥", scope: "연산자" },
    { title: "논리 연산자", href: "/coding/common/logical-operator", priority: "🔥", scope: "연산자" },
    { title: "비트 연산자", href: "/coding/common/bitwise-operator", priority: "🔥", scope: "연산자" },
    { title: "삼항 연산자", href: "/coding/c-language/ternary-operator", priority: "🔥", scope: "연산자" },
    { title: "연산자 우선순위", href: "/coding/common/operator-precedence", priority: "🔥", scope: "연산자" },
    // 제어문/반복문 (나머지)
    { title: "switch-case문과 fall-through", href: "/coding/common/switch-case", priority: "🔥", scope: "제어문/반복문" },
    // 함수 (나머지)
    { title: "사용자 정의 함수 기초", href: "/coding/c-language/user-defined-function", priority: "🤔", scope: "함수" },
    { title: "함수 프로토타입 (함수 선언)", href: "/coding/c-language/function-prototype", priority: "🤔", scope: "함수" },
    { title: "재귀함수 (Recursion)", href: "/coding/c-language/recursion", priority: "🤔", scope: "함수" },
    { title: "전설의 완전수 문제", href: "/coding/c-language/perfect-number", priority: "🔥", scope: "함수" },
    // 배열 (나머지)
    { title: "구조체 배열", href: "/coding/c-language/struct-array", priority: "🤔", scope: "구조체" },
    { title: "2차원 배열", href: "/coding/c-language/2d-array", priority: "🤔", scope: "배열" },
    // 문자열 (나머지)
    { title: "ASCII 코드 - 문자와 숫자의 관계", href: "/coding/common/ascii", priority: "⭐️", scope: "문자열" },
    { title: "ctype.h 문자 판별 함수", href: "/coding/c-language/ctype", priority: "🤔", scope: "문자열" },
    // 포인터 (나머지)
    { title: "이중 포인터 (Double Pointer)", href: "/coding/c-language/double-pointer", priority: "🔥", scope: "포인터" },
    { title: "포인터 배열과 2차원 배열", href: "/coding/c-language/pointer-array", priority: "🔥", scope: "포인터" },
    { title: "포인터 산술 (Pointer Arithmetic)", href: "/coding/c-language/pointer-arithmetic", priority: "🔥", scope: "포인터" },
    { title: "문자열 포인터 (char *)", href: "/coding/c-language/string-pointer", priority: "🔥", scope: "포인터" },
    { title: "포인터를 이용한 문자열 복사", href: "/coding/c-language/string-copy", priority: "🤔", scope: "포인터" },
    { title: "문자열 뒤집기 (포인터 활용)", href: "/coding/c-language/string-reverse", priority: "🤔", scope: "포인터" },
    { title: "구조체 포인터", href: "/coding/c-language/struct-pointer", priority: "🤔", scope: "구조체" },
    { title: "함수 포인터 (Function Pointer)", href: "/coding/c-language/function-pointer", priority: "🔥", scope: "포인터" },
    { title: "Call by Value vs Call by Reference", href: "/coding/common/call-by-value", priority: "🤔", scope: "함수" },
    // 메모리 (나머지)
    { title: "메모리 구조 (스택, 힙, 데이터 영역)", href: "/coding/common/memory-structure", priority: "🤔", scope: "기초" },
    { title: "static 키워드 - C언어와 Java 비교", href: "/coding/common/static", priority: "🤔", scope: "메모리" },
    { title: "static 변수 - 값이 유지되는 변수", href: "/coding/c-language/static-variable", priority: "🤔", scope: "메모리" },
    { title: "sizeof 연산자 (포인터 vs 배열)", href: "/coding/c-language/sizeof", priority: "🤔", scope: "구조체" },
    // 구조체 (나머지)
    { title: "typedef - 자료형에 새 이름 붙이기", href: "/coding/c-language/typedef", priority: "🤔", scope: "구조체" },
    { title: "중첩 구조체와 포인터 접근", href: "/coding/c-language/struct-nested", priority: "🤔", scope: "구조체" },
    // 자료구조
    { title: "스택 (Stack) - LIFO 자료구조", href: "/coding/c-language/stack", priority: "🤔", scope: "자료구조" },
    { title: "큐 (Queue) - FIFO 자료구조", href: "/coding/c-language/queue", priority: "🤔", scope: "자료구조" },
    { title: "원형 큐 (Circular Queue)", href: "/coding/c-language/circular-queue", priority: "🤔", scope: "자료구조" },
    { title: "연결 리스트 (Linked List)", href: "/coding/c-language/linked-list", priority: "🤔", scope: "자료구조" },
    { title: "연결 리스트 뒤집기 (Reverse Linked List)", href: "/coding/c-language/linked-list-reverse", priority: "🤔", scope: "자료구조" },
    { title: "이진 트리 (Binary Tree)", href: "/coding/c-language/binary-tree", priority: "🤔", scope: "자료구조" },
    // 알고리즘
    { title: "선택정렬 (Selection Sort)", href: "/coding/c-language/selection-sort", priority: "🔥", scope: "알고리즘" },
  ],
};

export const java: SubjectData = {
  name: "Java",
  image: "/coding/java/java_gamja.webp",
  href: "/coding/java",
  recommendedPosts: [
    // 폴더 노출 순서: 기초 → 입출력 → 연산자 → 제어문/반복문 → 배열/문자열 → 클래스/메서드 → 객체지향 → 알고리즘
    { title: "코드를 읽는 순서 (C vs Java vs Python)", href: "/coding/common/code-reading-order", priority: "", scope: "기초", essential: true },
    { title: "변수 선언", href: "/coding/common/variable-declaration", priority: "", scope: "기초", essential: true },
    { title: "변수의 초기화", href: "/coding/common/initialization", priority: "", scope: "기초", essential: true },
    { title: "Java 자료형 (int, boolean, char, 형변환)", href: "/coding/java/data-types", priority: "🤔", scope: "기초", essential: true },
    // 입출력함수
    { title: "출력 함수 (print, println, printf)와 문자열 결합", href: "/coding/java/output", priority: "⭐️", scope: "입출력함수" },
    // 연산자 폴더 첫 진입점
    { title: "산술·증감 연산자", href: "/coding/common/arithmetic-operator", priority: "🔥", scope: "연산자" },
    // 제어문/반복문 폴더 첫 진입점
    { title: "조건문 (if, if-else, else if)", href: "/coding/common/condition", priority: "⭐️", scope: "제어문/반복문" },
    { title: "반복문 (for, while, do-while)", href: "/coding/common/loop", priority: "🔥", scope: "제어문/반복문" },
    // 배열/문자열 폴더 첫 진입점
    { title: "배열의 기초", href: "/coding/common/array", priority: "🔥", scope: "배열/문자열" },
    { title: "문자열의 기초 (equals, String Pool)", href: "/coding/common/string", priority: "🔥", scope: "배열/문자열" },
    // 클래스/메서드 폴더 첫 진입점
    { title: "객체와 클래스 - 클래스, 인스턴스, 생성자", href: "/coding/java/objects-and-classes", priority: "🤔", scope: "클래스/메서드" },
    { title: "static 키워드 - 클래스 메서드와 클래스 변수", href: "/coding/java/static", priority: "⭐️", scope: "클래스/메서드" },
    { title: "static 키워드 - C언어와 Java 비교", href: "/coding/common/static", priority: "⭐️", scope: "클래스/메서드" },
    // 객체지향 폴더 첫 진입점
    { title: "상속(extends), super, this()와 필드 하이딩", href: "/coding/java/inheritance", priority: "💯", scope: "객체지향" },
    { title: "메서드 오버라이딩과 static 메서드", href: "/coding/java/override", priority: "⭐️", scope: "객체지향" },
    { title: "@Override 어노테이션", href: "/coding/java/override-annotation", priority: "", scope: "객체지향" },
  ],
  posts: [
    // 기초 (나머지)
    { title: "변수 스코프 (지역/전역 변수)", href: "/coding/common/variable-scope", priority: "", scope: "기초" },
    { title: "접근제어자 (public, private)", href: "/coding/java/access-modifier", priority: "🤔", scope: "기초" },
    { title: "컴파일과 런타임", href: "/coding/common/compile-runtime", priority: "", scope: "기초" },
    { title: "메모리 구조 (스택, 힙, 데이터 영역)", href: "/coding/common/memory-structure", priority: "🤔", scope: "기초" },
    // 연산자 (나머지)
    { title: "대입·복합 대입 연산자", href: "/coding/common/assignment-operator", priority: "🔥", scope: "연산자" },
    { title: "비교 연산자", href: "/coding/common/comparison-operator", priority: "🔥", scope: "연산자" },
    { title: "논리 연산자", href: "/coding/common/logical-operator", priority: "🔥", scope: "연산자" },
    { title: "비트 연산자", href: "/coding/common/bitwise-operator", priority: "🔥", scope: "연산자" },
    { title: "삼항 연산자", href: "/coding/java/ternary-operator", priority: "🔥", scope: "연산자" },
    { title: "연산자 우선순위", href: "/coding/common/operator-precedence", priority: "🔥", scope: "연산자" },
    // 제어문/반복문 (나머지)
    { title: "for-each문 (향상된 for문)", href: "/coding/java/for-each", priority: "⭐️", scope: "배열/문자열" },
    { title: "switch-case문과 fall-through", href: "/coding/common/switch-case", priority: "🔥", scope: "제어문/반복문" },
    // 배열/문자열 (나머지)
    { title: "ASCII 코드 - 문자와 숫자의 관계", href: "/coding/common/ascii", priority: "⭐️", scope: "배열/문자열" },
    { title: "객체 배열", href: "/coding/java/object-array", priority: "🔥", scope: "클래스/메서드" },
    { title: "매개변수 전달 방식 (Call by Value)", href: "/coding/common/call-by-value", priority: "🔥", scope: "배열/문자열" },
    { title: "기본 제공 클래스 (Integer, String, Math)", href: "/coding/java/built-in-classes", priority: "🔥", scope: "클래스/메서드" },
    { title: "String 불변성과 StringBuilder", href: "/coding/java/string-builder", priority: "🔥", scope: "배열/문자열" },
    // 클래스/메서드 (나머지)
    { title: "메서드 오버로딩", href: "/coding/java/overloading", priority: "🔥", scope: "클래스/메서드" },
    { title: "재귀 호출 (Recursion)", href: "/coding/java/recursion", priority: "🤔", scope: "클래스/메서드" },
    { title: "컴파일타임 바인딩", href: "/coding/java/compile-time-binding", priority: "⭐️", scope: "객체지향" },
    { title: "예외 처리 (try-catch)", href: "/coding/java/try-catch", priority: "🔥", scope: "클래스/메서드" },
    { title: "싱글톤 패턴 (Singleton)", href: "/coding/java/singleton", priority: "🤔", scope: "클래스/메서드" },
    // 객체지향 (나머지)
    { title: "객체지향 프로그래밍 vs 절차지향 프로그래밍", href: "/coding/common/oop-vs-procedural", priority: "🤔", scope: "기초" },
    { title: "추상 클래스와 abstract 키워드", href: "/coding/java/abstract-class", priority: "🔥", scope: "객체지향" },
    { title: "인터페이스와 implements", href: "/coding/java/interface", priority: "🔥", scope: "객체지향" },
    { title: "인터페이스의 default 메서드", href: "/coding/java/default-method", priority: "🤔", scope: "객체지향" },
    { title: "람다 표현식과 함수형 인터페이스", href: "/coding/java/lambda-expression", priority: "🤔", scope: "객체지향" },
    { title: "스레드 (Thread)", href: "/coding/java/thread", priority: "🤔", scope: "객체지향" },
    { title: "enum(열거형)과 주요 메서드", href: "/coding/java/enum", priority: "🔥", scope: "객체지향" },
    { title: "제네릭과 타입 소거 (Generic & Type Erasure)", href: "/coding/java/generic", priority: "🤔", scope: "객체지향" },
    // 심화 주제들
    { title: "instanceof와 안전 형변환", href: "/coding/java/instanceof", priority: "🤔", scope: "객체지향" },
    { title: "오토박싱·언박싱과 Integer 캐시", href: "/coding/java/autoboxing-integer-cache", priority: "🤔", scope: "클래스/메서드" },
    // 알고리즘
    { title: "정렬과 순위 알고리즘", href: "/coding/java/sorting-ranking", priority: "🤔", scope: "알고리즘" },
  ],
};

export const python: SubjectData = {
  name: "Python",
  image: "/coding/python/python_gamja.webp",
  href: "/coding/python",
  recommendedPosts: [
    // 폴더 노출 순서는 각 scope의 첫 등장 인덱스가 결정함 (groupPostsByScope의 Map 삽입 순서)
    // 입문 학습 흐름: 기초 → 연산자 → 제어문/반복문 → 함수 → 그 외
    // 필수 4종(essential)은 PostIcon 좌측 상단에 별도 뱃지로 표시. 기초 scope는 priority 미부여
    { title: "코드를 읽는 순서 (C vs Java vs Python)", href: "/coding/common/code-reading-order", priority: "", scope: "기초", essential: true },
    { title: "변수 선언", href: "/coding/common/variable-declaration", priority: "", scope: "기초", essential: true },
    { title: "변수의 초기화", href: "/coding/common/initialization", priority: "", scope: "기초", essential: true },
    { title: "자료형(int, float, str, tuple)", href: "/coding/python/data-types", priority: "🔥", scope: "기초", essential: true },
    { title: "변수 스코프 (지역/전역 변수)", href: "/coding/common/variable-scope", priority: "", scope: "기초" },
    { title: "컴파일과 런타임 (인터프리터)", href: "/coding/common/compile-runtime", priority: "", scope: "기초" },
    // 연산자 폴더 첫 진입점
    { title: "산술·증감 연산자", href: "/coding/common/arithmetic-operator", priority: "🔥", scope: "연산자" },
    // 제어문/반복문 폴더 첫 진입점
    { title: "조건문 (if, elif, else)", href: "/coding/common/condition", priority: "⭐️", scope: "제어문/반복문" },
    // 함수/클래스 폴더 첫 진입점 (lambda보다 함수의 기초가 먼저 와야 학습 흐름이 자연스러움)
    { title: "함수의 기초 (정의, 호출, 반환값)", href: "/coding/common/function", priority: "🤔", scope: "함수/클래스" },
    // 자료구조 폴더 (문자열·리스트·딕셔너리/집합 통합)
    { title: "리스트와 주요 메서드", href: "/coding/common/array", priority: "🔥", scope: "자료구조" },
    { title: "문자열 슬라이싱과 인덱싱", href: "/coding/common/string", priority: "💯", scope: "자료구조" },
    { title: "튜플(tuple)", href: "/coding/python/tuple", priority: "🔥", scope: "자료구조" },
    { title: "딕셔너리 (Dictionary)", href: "/coding/python/dict", priority: "🔥", scope: "자료구조" },
    { title: "집합 (Set)", href: "/coding/python/set", priority: "🔥", scope: "자료구조" },
    { title: "람다 표현식 (Lambda Expression)", href: "/coding/common/lambda", priority: "🤔", scope: "함수/클래스" },
    { title: "재귀 함수 (Recursion)", href: "/coding/python/recursion", priority: "🤔", scope: "함수/클래스" },
    { title: "출력 함수 (print)", href: "/coding/python/output-functions", priority: "🔥", scope: "함수/클래스" },
    { title: "기본 제공 함수 (range, len, type, sum, map)", href: "/coding/python/built-in-functions", priority: "🔥", scope: "함수/클래스" },
  ],
  posts: [
    { title: "언패킹과 다중 할당", href: "/coding/python/unpacking", priority: "🔥", scope: "자료구조" },
    { title: "클래스와 __init__", href: "/coding/python/class", priority: "🤔", scope: "함수/클래스" },
    { title: "대입·복합 대입 연산자", href: "/coding/common/assignment-operator", priority: "🔥", scope: "연산자" },
    { title: "비교 연산자", href: "/coding/common/comparison-operator", priority: "🔥", scope: "연산자" },
    { title: "논리 연산자", href: "/coding/common/logical-operator", priority: "🔥", scope: "연산자" },
    { title: "비트 연산자", href: "/coding/common/bitwise-operator", priority: "🤔", scope: "연산자" },
    { title: "연산자 우선순위", href: "/coding/common/operator-precedence", priority: "🔥", scope: "연산자" },
    { title: "이터러블 (iterable)", href: "/coding/python/iterable", priority: "🤔", scope: "자료구조" },
    { title: "제너레이터 표현식", href: "/coding/python/expression-sugar", priority: "🤔", scope: "자료구조" },
    { title: "컴프리헨션 통합 정리 (리스트·딕셔너리·집합·제너레이터)", href: "/coding/python/comprehension", priority: "🤔", scope: "자료구조" },
    { title: "얕은 복사와 깊은 복사 (Shallow/Deep Copy)", href: "/coding/python/shallow-copy", priority: "🤔", scope: "자료구조" },
    { title: "백슬래시 줄 연속", href: "/coding/python/line-continuation", priority: "🤔", scope: "함수/클래스" },
    { title: "반복문 (for, while)", href: "/coding/common/loop", priority: "⭐️", scope: "제어문/반복문" },
    { title: "메모리 구조 (스택, 힙, 데이터 영역)", href: "/coding/common/memory-structure", priority: "", scope: "기초" },
  ],
};

export const sql: SubjectData = {
  name: "SQL",
  image: "/coding/sql/sql_gamja.webp",
  href: "/coding/sql",
  recommendedPosts: [
    { title: "SQL 첫걸음 - 데이터베이스·테이블·SQL 이해하기", href: "/coding/sql/sql-intro", priority: "💯", scope: "입문" },
    { title: "SQL DDL과 DCL - 테이블 정의와 권한 관리", href: "/coding/sql/sql-ddl-dcl", priority: "⭐️", scope: "SQL 문법" },
    { title: "SQL DML - INSERT, UPDATE, DELETE로 데이터 조작하기", href: "/coding/sql/sql-dml", priority: "⭐️", scope: "SQL 문법" },
    { title: "SQL SELECT 완전 정복 - WHERE, ORDER BY, GROUP BY, 집계함수", href: "/coding/sql/sql-select", priority: "💯", scope: "SQL 문법" },
    { title: "SQL JOIN - 테이블 결합", href: "/coding/sql/sql-join", priority: "⭐️", scope: "SQL 문법" },
    { title: "SQL 기본 서브쿼리 - 단일 값, IN, ALL, ANY", href: "/coding/sql/sql-subquery", priority: "⭐️", scope: "SQL 문법" },
    { title: "SQL 고급 서브쿼리 - 다단계 중첩과 상관 서브쿼리", href: "/coding/sql/sql-advanced-subquery", priority: "🤔", scope: "SQL 문법" },
  ],
  posts: [],
};

export const common: SubjectData = {
  name: "공통 기초",
  image: "/coding/common/common_gamja.webp",
  href: "/coding/common",
  recommendedPosts: [],
  posts: [
    // 프로그래밍 기초 (순서: 변수선언 → 코드읽는순서 → 변수스코프 → 초기화 → OOP → 컴파일/런타임 → 메모리구조)
    { title: "변수 선언 방법", href: "/coding/common/variable-declaration", priority: "🤔", scope: "프로그래밍 기초" },
    { title: "코드를 읽는 순서 (C vs Java vs Python)", href: "/coding/common/code-reading-order", priority: "🤔", scope: "프로그래밍 기초" },
    { title: "변수 스코프 (지역 변수와 전역 변수)", href: "/coding/common/variable-scope", priority: "⭐️", scope: "프로그래밍 기초" },
    { title: "초기화란? (Initialization vs Reset)", href: "/coding/common/initialization", priority: "🤔", scope: "프로그래밍 기초" },
    { title: "객체지향 프로그래밍 vs 절차지향 프로그래밍", href: "/coding/common/oop-vs-procedural", priority: "🤔", scope: "프로그래밍 기초" },
    { title: "컴파일과 런타임의 차이", href: "/coding/common/compile-runtime", priority: "🤔", scope: "프로그래밍 기초" },
    { title: "메모리 구조 (스택, 힙, 데이터 영역)", href: "/coding/common/memory-structure", priority: "🤔", scope: "프로그래밍 기초" },
    // 수/진법
    { title: "이진수 (2진수) 변환", href: "/coding/common/binary", priority: "⭐️", scope: "수/진법" },
    { title: "ASCII 코드 - 문자와 숫자의 관계", href: "/coding/common/ascii", priority: "⭐️", scope: "수/진법" },
    { title: "16진수 (Hexadecimal)", href: "/coding/common/hexadecimal", priority: "⭐️", scope: "수/진법" },
    // 연산자
    { title: "산술·증감 연산자", href: "/coding/common/arithmetic-operator", priority: "🔥", scope: "연산자" },
    { title: "대입·복합 대입 연산자", href: "/coding/common/assignment-operator", priority: "🔥", scope: "연산자" },
    { title: "비교 연산자", href: "/coding/common/comparison-operator", priority: "🔥", scope: "연산자" },
    { title: "논리 연산자", href: "/coding/common/logical-operator", priority: "🔥", scope: "연산자" },
    { title: "비트 연산자", href: "/coding/common/bitwise-operator", priority: "🔥", scope: "연산자" },
    { title: "연산자 우선순위", href: "/coding/common/operator-precedence", priority: "🔥", scope: "연산자" },
    // 제어문/반복문
    { title: "조건문 (if, if-else, else if)", href: "/coding/common/condition", priority: "⭐️", scope: "제어문/반복문" },
    { title: "반복문 (for, while)", href: "/coding/common/loop", priority: "⭐️", scope: "제어문/반복문" },
    { title: "switch-case문과 fall-through", href: "/coding/common/switch-case", priority: "🔥", scope: "제어문/반복문" },
    // 배열/문자열
    { title: "배열과 리스트의 기초", href: "/coding/common/array", priority: "🔥", scope: "배열/문자열" },
    { title: "문자열의 기초", href: "/coding/common/string", priority: "🔥", scope: "배열/문자열" },
    { title: "함수의 기초", href: "/coding/common/function", priority: "🤔", scope: "함수" },
    { title: "Call by Value vs Call by Reference", href: "/coding/common/call-by-value", priority: "🔥", scope: "함수" },
    { title: "람다 표현식 (Lambda Expression)", href: "/coding/common/lambda", priority: "🤔", scope: "함수" },
  ],
};

// ─── 이론 과목 ───

export const db: SubjectData = {
  name: "DB",
  image: "/main/db_gamza.webp",
  href: "/theory/db",
  recommendedPosts: [
    { title: "데이터베이스 스키마 3단계 구조 (외부, 개념, 내부)", href: "/theory/db/database-schema", priority: "🤔", scope: "논리DB 설계" },
    { title: "데이터 모델 3요소와 요개논물(설계 4단계)", href: "/theory/db/data-model", priority: "⭐️", scope: "논리DB 설계" },
    { title: "관계 데이터 모델의 구성요소 - 릴레이션, 카디널리티, 차수", href: "/theory/db/relational-data-model-elements", priority: "⭐️", scope: "논리DB 설계" },
    { title: "관계대수와 관계해석 - 관계대수 기호", href: "/theory/db/relational-algebra", priority: "⭐️", scope: "논리DB 설계" },
    { title: "키(key) - 기본키, 외래키, 슈퍼키, 후보키", href: "/theory/db/db-key", priority: "🔥", scope: "물리DB 설계" },
    { title: "트랜잭션과 회복 기법", href: "/theory/db/transaction", priority: "🤔", scope: "트랜잭션" },
  ],
  posts: [
    { title: "RAID - 저장 장치의 성능과 안정성", href: "/theory/db/storage", priority: "🤔", scope: "물리DB 설계" },
    { title: "데이터베이스 파일 구조: 순차, 인덱스, 해싱 접근 접근 방식", href: "/theory/db/database-file-structure", priority: "🤔", scope: "물리DB 설계" },
    { title: "데이터베이스 이상 현상", href: "/theory/db/db-anomaly", priority: "🔥", scope: "물리DB 설계" },
    { title: "데이터베이스 함수 종속성", href: "/theory/db/functional-dependency", priority: "🔥", scope: "물리DB 설계" },
    { title: "정규화, 반정규화", href: "/theory/db/db-normalization", priority: "🤔", scope: "물리DB 설계" },
    { title: "데이터베이스 무결성: 개체, 참조, 속성, 사용자, 키", href: "/theory/db/data-integrity", priority: "🤔", scope: "물리DB 설계" },
  ],
};

export const networkOs: SubjectData = {
  name: "네트워크/OS",
  image: "/main/network_gamza.webp",
  href: "/theory/network-os",
  recommendedPosts: [
    { title: "네트워크 전송 방식: 패킷 교환 vs 서킷 교환", href: "/theory/network-os/network-transmission-method", priority: "🔥", scope: "네트워크 7계층" },
    { title: "네트워크 계층 프로토콜", href: "/theory/network-os/network-protocol", priority: "⭐️", scope: "네트워크 7계층" },
    { title: "IP 주소", href: "/theory/network-os/ip", priority: "🤔", scope: "IP" },
    { title: "메모리 페이지 교체 알고리즘", href: "/theory/network-os/memory-page-replacement", priority: "⭐️", scope: "메모리/프로세스" },
  ],
  posts: [
    { title: "Unix/Linux 기본 명령어 정리", href: "/theory/network-os/unix-commands", priority: "🔥", scope: "shell script" },
    { title: "리눅스 chmod 명령어 사용법 (8진수 권한 설정)", href: "/theory/network-os/chmod", priority: "🔥", scope: "shell script" },
    { title: "OSI 7계층 - 계층별 역할과 PDU 한눈에 정리", href: "/theory/network-os/osi-7-layer", priority: "🤔", scope: "네트워크 7계층" },
    { title: "응용 계층 프로토콜", href: "/theory/network-os/application-layer-protocol", priority: "⭐️", scope: "네트워크 7계층" },
    { title: "서브넷 마스크", href: "/theory/network-os/subnet-mask", priority: "🤔", scope: "IP" },
    { title: "CIDR 표기법과 프리픽스 길이", href: "/theory/network-os/cidr-prefix", priority: "🤔", scope: "IP" },
    { title: "라우터 인터페이스 IP 주소 구하기", href: "/theory/network-os/router-interface-ip", priority: "⭐️", scope: "IP" },
    { title: "서브넷 마스크 블록 크기(Block Size)", href: "/theory/network-os/subnet-mask-block-size", priority: "🤔", scope: "IP" },
    { title: "서브넷팅", href: "/theory/network-os/subnetting", priority: "🤔", scope: "IP" },
    { title: "라우팅 프로토콜 IGP, EGP 종류", href: "/theory/network-os/routing-protocol", priority: "⭐️", scope: "IP" },
    { title: "가상 메모리 - 페이지, 프레임, 페이지 테이블", href: "/theory/network-os/virtual-memory", priority: "🤔", scope: "메모리/프로세스" },
    { title: "프로세스 스케줄링 알고리즘", href: "/theory/network-os/process-scheduling-algorithm", priority: "⭐️", scope: "메모리/프로세스" },
    { title: "데이터 링크 프로토콜과 오류 제어", href: "/theory/network-os/data-link-protocol", priority: "🔥", scope: "네트워크 7계층" },
  ],
};

export const swDev: SubjectData = {
  name: "SW 개발",
  image: "/main/dev_gamza.webp",
  href: "/theory/sw-dev",
  recommendedPosts: [
    { title: "테스트 레벨(단위, 통합, 시스템, 인수)과 V 모델", href: "/theory/sw-dev/test-level", priority: "🔥", scope: "테스트" },
    { title: "테스트 커버리지 종류 구문(문장), 결정, 조건, MC DC", href: "/theory/sw-dev/test-coverage", priority: "⭐️", scope: "테스트" },
    { title: "데이터 표현 형식: JSON, XML, YAML", href: "/theory/sw-dev/interface-data-formats", priority: "🤔", scope: "인터페이스" },
    { title: "웹 서비스 인터페이스 - SOAP와 REST", href: "/theory/sw-dev/web-service-interface", priority: "🤔", scope: "인터페이스" },
  ],
  posts: [
    { title: "인터페이스 보안 암호화 프로토콜(PPTP, L2TP, SSL, S-HTTP)", href: "/theory/sw-dev/interface-security", priority: "🔥", scope: "인터페이스" },
    { title: "블랙박스 테스트 유형 정리", href: "/theory/sw-dev/blackbox-test", priority: "⭐️", scope: "테스트" },
    { title: "테스트 자동화(하네스) 구성요소", href: "/theory/sw-dev/test-auto-tool", priority: "🔥", scope: "테스트" },
    { title: "IPSec - AH, ESP, 전송/터널 모드", href: "/theory/sw-dev/ipsec", priority: "🔥", scope: "인터페이스" },
    { title: "테스트 케이스 구성 요소", href: "/theory/sw-dev/test-case", priority: "🔥", scope: "테스트" },
    { title: "인터페이스 통신 기술", href: "/theory/sw-dev/interface-communication-tech", priority: "🔥", scope: "인터페이스" },
    { title: "EAI와 ESB - 기업 애플리케이션 통합", href: "/theory/sw-dev/eai-esb", priority: "🤔", scope: "인터페이스" },
    { title: "형상 관리(SCM) - 4가지 활동과 도구(CVS·SVN·Git)", href: "/theory/sw-dev/software-configuration-management", priority: "🤔", scope: "통합구현" },
    { title: "URL 구조 - scheme, authority, path, query, fragment", href: "/theory/sw-dev/url-structure", priority: "🤔", scope: "인터페이스" },
  ],
};

export const swDesign: SubjectData = {
  name: "SW 설계",
  image: "/main/design_gamza.webp",
  href: "/theory/sw-design",
  recommendedPosts: [
    { title: "디자인패턴 암기방법", href: "/theory/sw-design/design-pattern-cheatsheet", priority: "⭐️", scope: "디자인패턴" },
  ],
  posts: [
    { title: "소프트웨어 모듈 응집도와 결합도", href: "/theory/sw-design/module-cohesion-coupling", priority: "⭐️", scope: "응집도/결합도" },
    { title: "UML 다이어그램", href: "/theory/sw-design/uml-diagram", priority: "🤔", scope: "UML" },
    { title: "UML 클래스 다이어그램의 6가지 관계", href: "/theory/sw-design/uml-class-diagram-relationships", priority: "🤔", scope: "UML" },
    { title: "소프트웨어 아키텍처 4+1 뷰 모델", href: "/theory/sw-design/4-1-view-model", priority: "🔥", scope: "UML" },
    { title: "객체 지향 설계 원칙 - SOLID", href: "/theory/sw-design/oop-solid", priority: "🔥", scope: "객체지향" },
    { title: "럼바우 객체 모델링 - 객(정)동기", href: "/theory/sw-design/rumbaugh-modeling", priority: "🤔", scope: "객체지향" },
    { title: "싱글톤 singleton 패턴", href: "/theory/sw-design/design-pattern-creation-singleton", priority: "🔥", scope: "디자인패턴" },
    { title: "팩토리 메서드 factory method 패턴", href: "/theory/sw-design/design-pattern-creation-factory_method", priority: "🔥", scope: "디자인패턴" },
    { title: "빌더 builder 패턴", href: "/theory/sw-design/design-pattern-creation-builder", priority: "⭐️", scope: "디자인패턴" },
    { title: "프로토타입 prototype 패턴", href: "/theory/sw-design/design-pattern-creation-prototype", priority: "💯", scope: "디자인패턴" },
    { title: "추상 팩토리 abstract factory 패턴", href: "/theory/sw-design/design-pattern-creation-abstract_factory", priority: "🔥", scope: "디자인패턴" },
    { title: "어댑터 adapter 패턴", href: "/theory/sw-design/design-pattern-structure-adapter", priority: "🔥", scope: "디자인패턴" },
    { title: "데코레이터 decorator 패턴", href: "/theory/sw-design/design-pattern-structure-decorator", priority: "⭐️", scope: "디자인패턴" },
    { title: "퍼사드 facade 패턴", href: "/theory/sw-design/design-pattern-structure-facade", priority: "⭐️", scope: "디자인패턴" },
    { title: "프록시 proxy 패턴", href: "/theory/sw-design/design-pattern-structure-proxy", priority: "🔥", scope: "디자인패턴" },
    { title: "브릿지 bridge 패턴", href: "/theory/sw-design/design-pattern-structure-bridge", priority: "🔥", scope: "디자인패턴" },
    { title: "플라이웨이트 flyweight 패턴", href: "/theory/sw-design/design-pattern-structure-flyweight", priority: "⭐️", scope: "디자인패턴" },
    { title: "컴포지트 composite 패턴", href: "/theory/sw-design/design-pattern-structure-composite", priority: "⭐️", scope: "디자인패턴" },
    { title: "전략 strategy 패턴", href: "/theory/sw-design/design-pattern-behavior-strategy", priority: "⭐️", scope: "디자인패턴" },
    { title: "옵저버 observer 패턴", href: "/theory/sw-design/design-pattern-behavior-observer", priority: "🔥", scope: "디자인패턴" },
    { title: "중재자 mediator 패턴", href: "/theory/sw-design/design-pattern-behavior-mediator", priority: "💯", scope: "디자인패턴" },
    { title: "방문자 visitor 패턴", href: "/theory/sw-design/design-pattern-behavior-visitor", priority: "🔥", scope: "디자인패턴" },
    { title: "이터레이터 iterator 패턴", href: "/theory/sw-design/design-pattern-behavior-iterator", priority: "🔥", scope: "디자인패턴" },
    { title: "상태 state 패턴", href: "/theory/sw-design/design-pattern-behavior-state", priority: "⭐️", scope: "디자인패턴" },
    { title: "인터프리터 interpreter 패턴", href: "/theory/sw-design/design-pattern-behavior-interpreter", priority: "⭐️", scope: "디자인패턴" },
    { title: "메멘토 memento 패턴", href: "/theory/sw-design/design-pattern-behavior-memento", priority: "⭐️", scope: "디자인패턴" },
    { title: "커맨드 command 패턴", href: "/theory/sw-design/design-pattern-behavior-command", priority: "⭐️", scope: "디자인패턴" },
    { title: "책임 연쇄 chain of responsibility 패턴", href: "/theory/sw-design/design-pattern-behavior-chain_of_responsibility", priority: "⭐️", scope: "디자인패턴" },
    { title: "템플릿 메서드 template method 패턴", href: "/theory/sw-design/design-pattern-behavior-template_method", priority: "⭐️", scope: "디자인패턴" },
    { title: "UI 설계 원칙", href: "/theory/sw-design/ui-design-principles", priority: "🤔", scope: "UI" },
    { title: "UI 종류 (CLI·GUI·NUI·OUI·VUI)", href: "/theory/sw-design/ui-types", priority: "🤔", scope: "UI" },
    { title: "애자일(Agile) 방법론", href: "/theory/sw-design/agile", priority: "🤔", scope: "방법론" },
    { title: "리팩토링(Refactoring)", href: "/theory/sw-design/refactoring", priority: "🤔", scope: "방법론" },
    { title: "코딩 컨벤션과 표기법", href: "/theory/sw-design/coding-convention", priority: "🤔", scope: "방법론" },
    { title: "소프트웨어 비용 산정 기법", href: "/theory/sw-design/cost-estimation", priority: "🤔", scope: "방법론" },
    { title: "비기능 요구사항", href: "/theory/sw-design/non-functional-requirements", priority: "🤔", scope: "요구사항" },
    { title: "릴리스 노트 작성 항목", href: "/theory/sw-design/release-note", priority: "🤔", scope: "제품 소프트웨어 패키징" },
  ],
};

export const securityNewtech: SubjectData = {
  name: "보안/신기술",
  image: "/main/security_gamza.webp",
  href: "/theory/security-newtech",
  recommendedPosts: [
    { title: "암호화 알고리즘", href: "/theory/security-newtech/cryptography-algorithms", priority: "🤔", scope: "암호 알고리즘" },
  ],
  posts: [
    { title: "네트워크 공격 종류", href: "/theory/security-newtech/network-attack", priority: "🔥", scope: "공격 유형" },
    { title: "DDoS vs DoS vs DRDoS", href: "/theory/security-newtech/dos-ddos-drdos", priority: "🤔", scope: "공격 유형" },
    { title: "서비스 거부 DoS 공격 종류", href: "/theory/security-newtech/dos-attack-types", priority: "🔥", scope: "공격 유형" },
    { title: "사회공학 공격 - 불특정 다수 타겟", href: "/theory/security-newtech/social-engineering-attacks", priority: "🔥", scope: "공격 유형" },
    { title: "표적 이메일 사기(BEC)", href: "/theory/security-newtech/social-engineering-targeted-attacks", priority: "🔥", scope: "공격 유형" },
    { title: "취약점 생명주기 - 제로데이, 1-day, 알려진 취약점", href: "/theory/security-newtech/vulnerability-lifecycle", priority: "🔥", scope: "공격 유형" },
    { title: "APT 공격 - 지능형 지속 위협", href: "/theory/security-newtech/apt-attack", priority: "🤔", scope: "공격 유형" },
    { title: "시스템 공격 - 레이스 컨디션, 심볼릭 링크, 권한 상승", href: "/theory/security-newtech/system-attacks", priority: "🔥", scope: "공격 유형" },
    { title: "소프트웨어 개발 보안 취약점 - 버퍼 오버플로, 포맷 스트링", href: "/theory/security-newtech/software-security-vulnerabilities", priority: "🤔", scope: "공격 유형" },
    { title: "웹 애플리케이션 공격 - SQL Injection, XSS, CSRF", href: "/theory/security-newtech/web-application-attacks", priority: "🤔", scope: "공격 유형" },
    { title: "악성 코드 종류", href: "/theory/security-newtech/malware", priority: "🔥", scope: "공격 유형" },
    { title: "정보보호 개요와 관리체계 - CIA, ISMS, CC, 법제", href: "/theory/security-newtech/information-protection", priority: "🔥", scope: "보안기능" },
    { title: "서버 접근 통제(Access Control) - DAC, MAC, RBAC", href: "/theory/security-newtech/server-access-control", priority: "🔥", scope: "보안기능" },
    { title: "3A : 인증, 권한 부여, 계정 관리", href: "/theory/security-newtech/authen-author-account", priority: "🤔", scope: "보안기능" },
    { title: "인증 기술 : SSO, Kerberos, OAuth", href: "/theory/security-newtech/authentication-tech", priority: "🔥", scope: "보안기능" },
    { title: "탐지·대응 보안 솔루션 - EDR, XDR, MDR, SIEM, SOAR", href: "/theory/security-newtech/security-detection-response", priority: "🤔", scope: "보안기능" },
    { title: "네트워크 방어 보안 솔루션 - 방화벽, IDS, IPS, WAF, UTM, NAC, Zero Trust", href: "/theory/security-newtech/security-network-defense", priority: "🤔", scope: "보안기능" },
    { title: "데이터 보호 보안 솔루션 - DLP, DRM", href: "/theory/security-newtech/security-data-protection", priority: "🔥", scope: "보안기능" },
    { title: "네트워크 신기술(애드혹, SDN, MQTT, MEC, NFV)", href: "/theory/security-newtech/network-new-tech", priority: "🤔", scope: "신기술 및 장비" },
    { title: "클라우드 & 가상화 신기술(도커, 쿠버네티스, 서버리스)", href: "/theory/security-newtech/cloud-new-tech", priority: "🤔", scope: "신기술 및 장비" },
    { title: "소프트웨어 신기술(SOA, CPS, MSA)", href: "/theory/security-newtech/sw-new-tech", priority: "🤔", scope: "신기술 및 장비" },
    { title: "데이터베이스 & 빅데이터 신기술", href: "/theory/security-newtech/db-new-tech", priority: "🤔", scope: "신기술 및 장비" },
  ],
};

// ─── 과목 그룹 ───

export const CODING_SUBJECTS: SubjectData[] = [common, cLanguage, java, python, sql];
export const THEORY_SUBJECTS: SubjectData[] = [db, networkOs, swDev, swDesign, securityNewtech];
